<template>
  <div class="labs-highlighted-carrousel">
    <ActionLoader
      v-if="!hits || hits.length === 0"
      class="labs-highlighted-carrousel__loader"
    />
    <Swiper
      v-else
      parentClass="labs-highlighted-carrousel__swiper"
      :swiperOptions="swiperOptions"
    >
      <div
        v-for="(lab, index) in hits"
        :key="lab.objectID"
        :class="[
          'swiper-slide',
          'labs-highlighted-carrousel__slide',
          `labs-highlighted-carrousel__slide--${labsHighlightedBgs[index]}`,
          !isSkillDiveB2BUser &&
            'labs-highlighted-carrousel__slide--skill-dive-banner',
        ]"
      >
        <div class="labs-highlighted-carrousel__title">
          {{ lab.name }}
        </div>
        <div class="labs-highlighted-carrousel__description">
          <span v-html="parseDescription(lab.description)"></span>
        </div>
        <div class="labs-highlighted-carrousel__details-wrapper">
          <div
            v-if="showAddedDaysAgo(lab.created)"
            class="labs-highlighted-carrousel__details-created"
          >
            {{ addedTimeAgoLabel(lab.created) }}
          </div>
          <div
            v-if="showAddedDaysAgo(lab.created)"
            class="labs-highlighted-carrousel__details-separator"
          ></div>
          <div
            v-if="lab.learning_areas && lab.learning_areas.length"
            class="labs-highlighted-carrousel__details-learning-area__wrapper"
          >
            <div
              v-for="(learningArea, labIndex) in lab.learning_areas"
              :key="learningArea.id"
              class="labs-highlighted-carrousel__details-learning-area__wrapper"
            >
              <LearningAreaLabel
                class="labs-highlighted-carrousel__details-learning-area"
                :learningArea="learningArea.name"
                uppercase
                extraSmallMobile
              />
              <div
                v-if="labIndex < lab.learning_areas.length - 1"
                class="labs-highlighted-carrousel__details-separator"
              ></div>
            </div>
          </div>
          <div
            v-if="lab.difficulty"
            class="labs-highlighted-carrousel__details-separator"
          ></div>
          <Difficulty
            v-if="lab.difficulty"
            class="labs-highlighted-carrousel__details-difficulty"
            :difficulty="lab.difficulty"
            :alwaysVisible="true"
            hideIconOnMobile
            uppercase
          />
          <div class="labs-highlighted-carrousel__details-separator"></div>
          <div class="labs-highlighted-carrousel__details-duration">
            {{ durationFormatted(lab) }}
            <span class="labs-highlighted-carrousel__details-duration-label"
              >&nbsp;Estimated time</span
            >
          </div>
        </div>
        <div class="labs-highlighted-carrousel__link-wrapper">
          <router-link
            v-if="isLabExperienceUser || labExperienceEnhance"
            class="labs-highlighted-carrousel__link btn btn--primary"
            :to="{ name: 'lab-page', params: { labId: lab.objectID } }"
          >
            Go to lab
          </router-link>
          <a
            v-else
            :class="[
              'labs-highlighted-carrousel__link',
              'btn',
              'btn--primary',
              isLoading && 'labs-highlighted-carrousel__link--disabled',
            ]"
            @click="getOrRequestAccess"
          >
            {{
              isBusinessUser && !isAccOwnerOrTeamManager
                ? 'Request access'
                : 'Get access'
            }}
          </a>
        </div>
      </div>
    </Swiper>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { formatDistanceToNow, differenceInDays } from 'date-fns'
import Difficulty from '@/components/properties/Difficulty.vue'
import LearningAreaLabel from '@/components/properties/LearningAreaLabel.vue'
import Swiper from '@/components/layout/Swiper.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import { algoliaSearchMixin, licenseAddonCommons } from '@/helpers/mixins'
import { getLearningAreaName } from '@/helpers'
import CONSTANTS from '@/constants'

export default {
  name: 'LabsHighlightedCarrousel',
  components: {
    Difficulty,
    LearningAreaLabel,
    Swiper,
    ActionLoader,
  },
  mixins: [algoliaSearchMixin, licenseAddonCommons],
  data() {
    return {
      swiperOptions: {
        loop: true,
        effect: 'slide',
        speed: 2500,
        autoplay: {
          delay: 7000,
        },
        freeMode: false,
        simulateTouch: window.innerWidth <= 1024,
        // DOCUMENTATION: https://swiperjs.com/swiper-api#pagination
        pagination: {
          el: '.swiper-pagination',
          type: 'bullets',
          clickable: true,
        },
      },
      labsHighlighted: [],
      labsHighlightedBgs: [
        'blue-bg',
        'bordo-bg',
        // 'light-blue-bg',
        'orange-bg',
        'pink-bg',
        'red-bg',
      ],
      checkoutUrl: `https://${process.env.VUE_APP_CHECKOUT_URL}`,
      skillDiveEngage: `https://${process.env.VUE_APP_SKILL_DIVE_ENGANGE}`,
      isLoading: false,
    }
  },
  computed: {
    ...mapState({
      algoliaKeys: state => state.search.algoliaKeys,
      available_passes: state => state.auth.available_passes,
    }),
    ...mapGetters({
      profileInterestAreas: 'auth/profileInterestAreas',
      isSkillDiveB2CUser: 'auth/isSkillDiveB2CUser',
      isSkillDiveB2BUser: 'auth/isSkillDiveB2BUser',
      isLabExperienceUser: 'auth/isLabExperienceUser',
      isAccOwnerOrTeamManager: 'auth/isAccOwnerOrTeamManager',
      labExperienceEnhance: 'app/labExperienceEnhance',
      isBusinessUser: 'business/isBusinessUser',
    }),
    hits() {
      return this.getIndexHits(this.algoliaKeys.searchIndex)
    },
  },
  async created() {
    const {
      algoliaKeys: { searchIndex },
    } = this

    const algoliaHelper = await this.initializeAlgoliaSearch(searchIndex, {
      // We are not sure if trending or featured will be ready for the first release of contents
      // facets: ['content_type', 'is_lab_experience', 'is_trending'],
      facets: ['content_type', 'is_lab_experience'],
      disjunctiveFacets: ['learning_areas.name'],
      hitsPerPage: 5,
    })

    algoliaHelper
      .addFacetRefinement('content_type', 'lab')
      .addFacetRefinement('is_lab_experience', 'true')
    // We are not sure if trending or featured will be ready for the first release of contents
    // .addFacetRefinement('is_trending', 'true')

    let learningAreaFilters = []

    if (this.isSkillDiveB2CUser) {
      learningAreaFilters = this.available_passes
        .map(pass => CONSTANTS.SKILL_DIVE_B2C_PASSES_LEARNING_AREAS[pass.id])
        .filter(learningArea => learningArea)
      learningAreaFilters = Object.values(learningAreaFilters)
    } else {
      learningAreaFilters = this.profileInterestAreas?.length
        ? CONSTANTS.LAB_EXPERIENCE.LEARNING_AREAS.filter(learningArea =>
            this.profileInterestAreas.some(area => area.id === learningArea.id)
          )
        : []
    }
    learningAreaFilters.forEach(learningArea =>
      algoliaHelper.addDisjunctiveFacetRefinement(
        'learning_areas.name',
        learningArea.name
      )
    )

    algoliaHelper.search()
  },
  methods: {
    totalTime(lab) {
      return this.$duration.durationFromSecs(lab.duration, 'minutes')
    },
    learningAreaName(lab) {
      const la = getLearningAreaName(lab)
      return la !== 'INE' ? la : this.learningArea
    },
    formatedDateFromApi(published_date) {
      // TODO reuse common code with collection mixin
      if (typeof published_date === 'string') {
        // comes string from content-api
        return new Date(published_date)
      } else {
        // comes number from algolia
        return new Date(published_date * 1000)
      }
    },
    addedTimeAgoLabel(date) {
      // TODO reuse common code with collection mixin
      const published_date = this.formatedDateFromApi(date)
      const days = differenceInDays(new Date(), new Date(published_date))
      if (days >= 15 && days <= 30) {
        return 'Recently added'
      }
      const timeAgo = formatDistanceToNow(published_date, {
        addSuffix: true,
      })

      return `Added ${timeAgo}`
    },
    showAddedDaysAgo(date) {
      // TODO reuse common code with collection mixin
      const addedTimeAgoLabel = this.addedTimeAgoLabel(date)
      return addedTimeAgoLabel.match(/month|year/) ? false : addedTimeAgoLabel
    },
    durationFormatted(lab) {
      const durationParts = this.$duration.hhmmss(lab.duration).split(':')
      let durationString = ''
      if (durationParts[0] > 1) {
        durationString = `${durationParts[0]}${
          durationParts[0] > 1 ? 'h' : 'h'
        } ${durationParts[1]}m`
      } else {
        durationString = `${durationParts[1]}m`
      }
      return durationString
    },
    parseDescription(desc) {
      const cleanDesc = desc
        ? desc
            .replace(/(<([^>]+)>)/gi, '')
            .replace(/(\r\n|\n|\r)/gm, ' ')
            .trim()
        : ''
      return cleanDesc.length > 200
        ? `${cleanDesc
            .slice(0, 200)
            .split(' ')
            .slice(0, -1)
            .join(' ')}...`
        : cleanDesc
    },
    async getOrRequestAccess() {
      if (this.isBusinessUser && !this.isAccOwnerOrTeamManager) {
        if (!this.isLoading) {
          this.isLoading = true
          await this.requestAddonLicense('skill-dive')
          this.isLoading = false
        }
      } else {
        window.location.href = this.isAccOwnerOrTeamManager
          ? this.skillDiveEngage
          : this.checkoutUrl
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.labs-highlighted-carrousel {
  display: flex;
  margin-bottom: 30px;
  &__loader {
    margin: auto;
    width: 100%;
    min-height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__slide {
    padding: 120px 0 40px 0;
    height: auto;
    @include desktop {
      min-height: 350px;
    }
    &--skill-dive-banner {
      padding-top: 120px;
      @include tablet {
        padding-top: 200px;
      }
    }
    &--blue-bg {
      background: #3113eb;
      background: linear-gradient(
        180deg,
        #3113eb 0%,
        rgba(49, 19, 235, 0) 100%
      );
      background-blend-mode: lighten;
      @include desktop {
        background-image: url('../../assets/images/lab-experience/lab-experience-blue-bg.png'),
          linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, transparent 100%),
          url('../../assets/images/backgrounds/dashboard_bg.jpg');
        background-repeat: no-repeat;
        background-position: top;
        background-size: 115%;
      }
    }
    &--bordo-bg {
      background: #9e0b4d;
      background: linear-gradient(
        180deg,
        #9e0b4d 0%,
        rgba(181, 81, 166, 0) 94.79%
      );
      background-blend-mode: lighten;
      @include desktop {
        background-image: url('../../assets/images/lab-experience/lab-experience-bordo-bg.png'),
          linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, transparent 100%),
          url('../../assets/images/backgrounds/dashboard_bg.jpg');
        background-repeat: no-repeat;
        background-position: top;
        background-size: 115%;
      }
    }
    &--light-blue-bg {
      background-blend-mode: hard-light;
      background: #07bde1;
      background: linear-gradient(
        180deg,
        #07bde1 0%,
        rgba(36, 167, 208, 0) 94.79%
      );
      @include desktop {
        background-image: url('../../assets/images/lab-experience/lab-experience-light-blue-bg.png'),
          linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, transparent 100%),
          url('../../assets/images/backgrounds/dashboard_bg.jpg');
        background-repeat: no-repeat;
        background-position: top;
        background-size: 115%;
      }
    }
    &--orange-bg {
      background: #ff8333;
      background: linear-gradient(
        180deg,
        #ff8333 0%,
        rgba(254, 131, 52, 0) 94.79%
      );
      background-blend-mode: lighten;
      @include desktop {
        background-image: url('../../assets/images/lab-experience/lab-experience-orange-bg.png'),
          linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, transparent 100%),
          url('../../assets/images/backgrounds/dashboard_bg.jpg');
        background-repeat: no-repeat;
        background-position: top;
        background-size: 115%;
      }
    }
    &--pink-bg {
      background: #cf4cf0;
      background: linear-gradient(
        180deg,
        #cf4cf0 0%,
        rgba(187, 18, 229, 0) 100%
      );
      background-blend-mode: lighten;
      @include desktop {
        background-image: url('../../assets/images/lab-experience/lab-experience-pink-bg.png'),
          linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, transparent 100%),
          url('../../assets/images/backgrounds/dashboard_bg.jpg');
        background-repeat: no-repeat;
        background-position: top;
        background-size: 115%;
      }
    }
    &--red-bg {
      background: rgba(255, 0, 0, 0.83);
      background: linear-gradient(
        180deg,
        rgba(255, 0, 0, 0.83) 0%,
        rgba(255, 0, 0, 0) 94.79%
      );
      background-blend-mode: lighten;
      @include desktop {
        background-image: url('../../assets/images/lab-experience/lab-experience-bordo-bg.png'),
          linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, transparent 100%),
          url('../../assets/images/backgrounds/dashboard_bg.jpg');
        background-repeat: no-repeat;
        background-position: top;
        background-size: 115%;
      }
    }
  }
  &__title {
    font-style: normal;
    font-weight: 700;
    font-size: rem(32px);
    line-height: rem(38px);
    padding: 0 0 20px 0;
    text-align: center;
    margin: 0 20px;
    max-width: 1200px;

    @include mobile {
      font-size: rem(40px);
      line-height: rem(52px);
      padding: 0 0 30px 0;
      margin: 0 30px;
    }
    @include desktop {
      margin: auto;
    }
  }
  &__description {
    font-size: rem(14px);
    font-style: normal;
    line-height: rem(20px);
    color: var(--athens-gray);
    font-weight: 450;
    text-align: center;
    margin: auto;
    margin-bottom: 15px;
    max-width: 1200px;
    width: 80%;

    max-height: 95px;
    overflow: hidden;

    @include mobile {
      width: 70%;
      font-size: rem(16px);
      line-height: rem(24px);
      max-height: 110px;
      overflow: hidden;
    }

    @include tablet {
      width: 54%;
    }
  }
  &__details-learning-area__wrapper {
    display: flex;
    gap: 10px;
  }

  &__details {
    &-wrapper {
      display: flex;
      align-items: center;
      width: fit-content;
      margin: 0 auto 40px auto;
      border-radius: 7px;
      background: var(--glass-dark);
      font-size: 10px !important;

      padding: 5px 10px;
      column-gap: 6px;

      @include mobile {
        column-gap: 20px;
        padding: 5px 20px;
        font-size: 12px !important;
      }
      @include desktop {
        margin: 0 auto 75px auto;
      }
    }
    &-separator {
      width: 1px;
      height: 30px;
      background: var(--glass-solid-mid-gray);
    }
    &-created {
      color: var(--purple-pale);
    }
    &-difficulty {
      width: unset !important;
      text-transform: uppercase !important;
    }
    &-duration {
      font-family: var(--font-book);
      text-align: left;
      text-transform: uppercase;
      font-weight: 450;
      display: flex;

      @include mobile {
        font-weight: 700;
      }

      &-label {
        font-family: var(--font-book);
        display: none;
        font-weight: normal;

        @include mobile {
          display: block;
        }
      }
    }
  }
  &__link {
    width: 90%;
    @include mobile {
      width: inherit;
    }
    &--disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  ::v-deep .difficulty__label {
    font-family: var(--font-book);
  }
}
</style>
